import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileDown, Trash2, FileCheck2 } from "lucide-react";
import { fdate, openPdf } from "./shared";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

// Scans signés (contrat ou avenant) — object storage, 10 Mo max
export function ContractFiles({ contract, onChanged }) {
  const c = contract;
  const ref = useRef(null);
  const [target, setTarget] = useState("contract");
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (target !== "contract") fd.append("amendment_id", target);
      const { data } = await api.post(`/chef/contrats/${c.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Scan signé enregistré — le document est marqué signé"); onChanged(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async (f) => {
    if (!window.confirm(`Supprimer « ${f.title} » ?`)) return;
    try { await api.delete(`/chef/contrats/files/${f.id}`); toast.success("Fichier supprimé"); const { data } = await api.get(`/chef/contrats/${c.id}`); onChanged(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const amendLabel = (id) => { const a = (c.amendments || []).find((x) => x.id === id); return a ? `Avenant n°${a.numero}` : "Contrat"; };

  return (
    <div className="space-y-4" data-testid="ct-files">
      <div className="border border-dashed border-[#002FA7] p-4 bg-[#F8F9FF] space-y-3">
        <div className="text-xs text-[#495057]">Déposez le contrat (ou l'avenant) <b>signé par les deux parties</b> : la case « Contrat signé » de la conformité se coche automatiquement. PDF, PNG, JPG ou WEBP, 10 Mo max.</div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={target} onValueChange={setTarget}><SelectTrigger data-testid="ct-file-target" className="h-10 w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="contract">Contrat initial</SelectItem>{(c.amendments || []).map((a) => <SelectItem key={a.id} value={a.id}>Avenant n°{a.numero}</SelectItem>)}</SelectContent></Select>
          <input ref={ref} type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" data-testid="ct-file-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button onClick={() => ref.current?.click()} disabled={busy} data-testid="ct-file-upload" className="h-10 bg-[#002FA7] hover:bg-[#002175] text-white"><Upload className="h-4 w-4 mr-2" />{busy ? "Envoi…" : "Déposer le scan signé"}</Button>
          <Button variant="outline" onClick={() => openPdf(`/chef/contrats/${c.id}/pdf`)} data-testid="ct-file-pdf" className="h-10"><FileDown className="h-4 w-4 mr-2" />PDF du contrat à signer</Button>
        </div>
      </div>
      {c.files.length === 0 ? <div className="text-sm text-[#868E96]" data-testid="ct-files-empty">Aucun scan déposé{c.signed_at ? ` — date de signature saisie : ${fdate(c.signed_at)}` : ""}.</div> : (
        <div className="space-y-1" data-testid="ct-files-list">
          {c.files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-sm border-b border-[#F1F3F5] py-2 last:border-b-0" data-testid={`ct-file-${f.id}`}>
              <FileCheck2 className="h-4 w-4 text-[#059669] shrink-0" />
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{f.title} <span className="text-[10px] text-[#868E96] font-normal">· {amendLabel(f.amendment_id)}</span></div><div className="text-[11px] text-[#868E96] truncate">{f.filename} · {(f.size / 1024).toFixed(0)} Ko · {fmt(f.uploaded_at)} · {f.uploaded_by}</div></div>
              <Button size="sm" variant="outline" onClick={() => openPdf(`/files/hr-contract/${f.id}`, f.filename)} data-testid={`ct-file-open-${f.id}`} className="h-8 px-2" title="Ouvrir"><FileDown className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(f)} data-testid={`ct-file-delete-${f.id}`} className="h-8 px-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white" title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
